import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AdminApi, AdminTestObject, AdminTestPaper } from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const AdminTestsPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminTestPaper[]>([])
  const [objects, setObjects] = useState<AdminTestObject[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [objectId, setObjectId] = useState<number | undefined>()
  const [isActive, setIsActive] = useState<number>(1)

  const objectNameById = useMemo(() => {
    const map = new Map<number, string>()
    objects.forEach((o) => map.set(o.id, o.name))
    return map
  }, [objects])

  const loadData = async (overrides?: {
    page?: number
    q?: string
    objectId?: number | undefined
    isActive?: number
  }) => {
    const nextPage = overrides?.page ?? page
    const nextQ = overrides && 'q' in overrides ? overrides.q : q
    const nextObjectId = overrides && 'objectId' in overrides ? overrides.objectId : objectId
    const nextActive = overrides && 'isActive' in overrides ? overrides.isActive! : isActive

    setLoading(true)
    try {
      const res = await AdminApi.listTestPapers({
        q: nextQ?.trim() || undefined,
        objectId: nextObjectId,
        isActive: nextActive,
        page: nextPage,
        limit: 20,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      toast.error('Không tải được danh sách bài test')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      try {
        const res = await AdminApi.listTestObjects()
        setObjects(res.data || [])
      } catch {
        toast.error('Không tải được danh sách đối tượng test')
      }
    })()
  }, [])

  useEffect(() => {
    void loadData({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const applyFilters = () => {
    if (page === 1) void loadData({ page: 1 })
    else setPage(1)
  }

  const resetFilters = () => {
    setQ('')
    setObjectId(undefined)
    setIsActive(1)
    if (page !== 1) setPage(1)
    void loadData({ page: 1, q: '', objectId: undefined, isActive: 1 })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-m2-on-surface">Quản lý bài test</h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            Soạn đề từ ngân hàng câu hỏi, xem đề, gắn skill Ask AI.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-m2-on-surface-variant">{total} bài test</p>
          <Link
            to="/admin/tests/new"
            className="rounded-lg bg-m2-primary px-4 py-2 text-sm font-semibold text-m2-on-primary"
          >
            Tạo bài test
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-xs font-semibold text-m2-on-surface">Tìm theo tên</label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Nhập tên bài test…"
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none"
          />
        </div>
        <div className="w-full sm:w-56">
          <label className="mb-1 block text-xs font-semibold text-m2-on-surface">Đối tượng</label>
          <select
            value={objectId ?? ''}
            onChange={(e) => setObjectId(e.target.value ? Number(e.target.value) : undefined)}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm text-m2-on-surface"
          >
            <option value="">Tất cả</option>
            {objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-40">
          <label className="mb-1 block text-xs font-semibold text-m2-on-surface">Trạng thái</label>
          <select
            value={isActive}
            onChange={(e) => setIsActive(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm text-m2-on-surface"
          >
            <option value={1}>Đang active</option>
            <option value={0}>Không active</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-lg bg-m2-primary px-4 py-2 text-sm font-semibold text-m2-on-primary"
          >
            Lọc
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-lg border border-m2-outline-variant px-4 py-2 text-sm font-medium text-m2-on-surface-variant"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-m2-outline-variant bg-m2-surface-high text-xs uppercase tracking-wide text-m2-on-surface-variant">
              <tr>
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Tên bài test</th>
                <th className="px-4 py-3 font-semibold">Đối tượng</th>
                <th className="px-4 py-3 font-semibold">Câu hỏi</th>
                <th className="px-4 py-3 font-semibold">Thời gian</th>
                <th className="px-4 py-3 font-semibold">Skill Ask AI</th>
                <th className="px-4 py-3 font-semibold">Tạo lúc</th>
                <th className="px-4 py-3 font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-m2-outline-variant">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-m2-on-surface-variant">
                    Đang tải…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-m2-on-surface-variant">
                    Không có bài test nào
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="hover:bg-m2-surface-low/80">
                    <td className="px-4 py-3 text-m2-on-surface-variant">{row.id}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-m2-on-surface">{row.name}</div>
                      {row.description ? (
                        <div className="mt-0.5 line-clamp-1 text-xs text-m2-on-surface-variant">
                          {String(row.description).replace(/<[^>]*>/g, '')}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-m2-on-surface-variant">
                      {row.objectId != null ? objectNameById.get(row.objectId) || `#${row.objectId}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-m2-on-surface">{row.totalQuestion ?? '—'}</td>
                    <td className="px-4 py-3 text-m2-on-surface-variant">
                      {row.minute != null ? `${row.minute} phút` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                          Number(row.skillLinkCount || 0) > 0
                            ? 'bg-m2-primary/15 text-m2-primary'
                            : 'bg-m2-surface-high text-m2-on-surface-variant'
                        }`}
                      >
                        {Number(row.skillLinkCount || 0)} skill
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-m2-on-surface-variant">
                      {formatDbDateTimeVi(row.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Link
                          to={`/admin/tests/${row.id}/edit`}
                          className="inline-flex rounded-lg border border-m2-outline-variant px-3 py-1.5 text-xs font-semibold text-m2-on-surface hover:bg-m2-surface-low"
                        >
                          Soạn đề
                        </Link>
                        <Link
                          to={`/admin/tests/${row.id}`}
                          className="inline-flex rounded-lg border border-m2-primary/30 bg-m2-primary/10 px-3 py-1.5 text-xs font-semibold text-m2-primary hover:bg-m2-primary/15"
                        >
                          Gắn skill / Xem
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-m2-outline-variant px-4 py-3 text-sm">
            <span className="text-m2-on-surface-variant">
              Trang {page}/{totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-m2-outline-variant px-3 py-1.5 disabled:opacity-40"
              >
                Trước
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-md border border-m2-outline-variant px-3 py-1.5 disabled:opacity-40"
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminTestsPage
